import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { appRepo } from "../../repo/app-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import AppVersionFormFields, {
  emptyAppVersionForm,
  isAppVersionFormValid,
  toAppVersionPayload,
} from "./app-version-form-fields.tsx";

export default function EditAppVersion() {
  const navigate = useNavigate();
  const { id } = useParams();
  const versionId = Number(id);
  const { setMessage } = useContext(AppContext);
  const [form, setForm] = useState(emptyAppVersionForm);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!versionId) {
      setMessage({ type: "error", message: "شناسه نسخه معتبر نیست." });
      navigate("/admin/app-versions");
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const response = await appRepo.getAppVersion(versionId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت نسخه ناموفق بود." });
          navigate("/admin/app-versions");
          return;
        }
        const data = response.data;
        setForm({
          os: data.os ?? "",
          version: String(data.version ?? ""),
          version_name: data.version_name ?? "",
          link: data.link ?? "",
          is_force_update: Boolean(data.is_force_update),
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/app-versions");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [versionId, navigate, setMessage]);

  const onChange = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAppVersionFormValid(form) || !versionId) {
      setMessage({ type: "error", message: "لطفاً همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await appRepo.updateAppVersion(versionId, toAppVersionPayload(form));
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش نسخه ناموفق بود." });
        return;
      }

      setMessage({ type: "success", message: "نسخه با موفقیت ویرایش شد." });
      navigate("/admin/app-versions");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش نسخه اپ" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال بارگذاری...
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش نسخه اپ" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <AppVersionFormFields form={form} onChange={onChange} />
          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isAppVersionFormValid(form) || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره…" : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/app-versions"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
